import { Category } from "@/apps/shared/catalog/core/domain/Category/Category";
import { Identifier } from "@/apps/shared/catalog/core/domain/Category/Identifier";
import { CategoryResponse } from "@/apps/shared/catalog/core/infrastructure/repository/categoryTreeRepository";
import { ProductCount } from "@/apps/shared/catalog/core/domain/Category/ProductCount";
import { Image } from "@/apps/shared/catalog/core/domain/Category/Image";

export type StoredCategory = {
  id: string;
  label: string;
  identifier: string;
  childrenDescription: string;
  /**
   * `children` may be a string in case it has children, but are not loaded yet.
   * In that cas, the field will have an empty string value.
   */
  children: StoredCategory[] | null;
  hasChildren: boolean;
  image: { url: string; alt: string } | null;
  productCount: { web: number; store: number };
};

export const fromResponseToModel = (category: CategoryResponse): Category => {
  const hasChildren = undefined !== category.children;
  const normalisedChildren: CategoryResponse[] | null =
    typeof category.children === "string" ? null : category.children;
  const children = (normalisedChildren ?? []).map((child) => fromResponseToModel(child));

  return new Category(
    category.id,
    category.label,
    new Identifier(category.identifier),
    category.children_description,
    children,
    hasChildren,
    category.image ? new Image(category.image.url, category.image.alt) : null,
    category.product_count
      ? new ProductCount(category.product_count.web, category.product_count.store)
      : new ProductCount()
  );
};

export const fromModelToStoredCategory = (category: Category): StoredCategory => {
  let children;
  if (Array.isArray(category?.children)) {
    children = category.children.map((child: Category) => fromModelToStoredCategory(child));
  } else {
    children = category.children;
  }

  const image = category.image ? { url: category.image.url, alt: category.image.alt } : null;

  return {
    id: category.id,
    label: category.label,
    identifier: String(category.identifier),
    childrenDescription: category.childrenDescription,
    children,
    hasChildren: category.hasChildren,
    image,
    productCount: category.productCount,
  };
};

export const storeCategoryToModel = (category: StoredCategory): Category => {
  const children = (category.children ?? []).map((child) => storeCategoryToModel(child));

  return new Category(
    category.id,
    category.label,
    new Identifier(category.identifier),
    category.childrenDescription,
    children,
    category.hasChildren,
    category.image ? new Image(category.image.url, category.image.alt) : null,
    new ProductCount(category.productCount.web, category.productCount.store)
  );
};
